// DS: C109
// @dynamic-i18n-keys
//   watchSuggestionsV2.userSampleGallery.joinedDateDistance
//   watchSuggestionsV2.userSampleGallery.joinedDateDistance.mobile
import React, { useContext, useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiUser, PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { ResourceType } from '../../types/resource';
import DeviationDuperbrowseContext from '../../Duperbrowse/DeviationDuperbrowseContext';
import { DeviationTypes } from '../../types/deviation';
import { dateToDistanceTrans } from '@wix/da-i18next/pkg/time/functions';
import { getDeviationType } from '../../DeviationViews/_helpers';
import DeviationLink from '../../Duperbrowse/DeviationLink';
import DeviationDumbView from '../../DeviationViews/Dumbo';
import UserAvatar from '../../User/Avatar/UserAvatar';
import UserLinkWithTooltip from '../../User/Link/UserLinkWithTooltip';
import Flex from '@wix/da-ds/pkg/Flex';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import Slider from '@wix/da-ds/pkg/Slider';
import UserWatchGhostButton from '../UserWatchGhostButton';
import { isGroup as getIsGroup } from '../../User/helpers';

import s from './UserSampleGallery.scss';

const DESKTOP_THUMB_DIMS = { width: 163, height: 110 };
const MOBILE_THUMB_DIMS = { width: 117, height: 78 };
const BI_SECTIONNAME = 'watch_suggestions';

export interface Props {
  user: PapiUser;
  deviations?: PapiDeviation[];
  isOwnUser?: boolean;
  dataHook?: string;
  /** the 'x' in 'back to x' when showing a user's gallery deviation in duperbrowse */
  duperbrowseContextTitle?: string;
  onWatch?: () => void;
  className?: string;
}

/** Gruser blurb, watch button, and small row of their deviations */
export const UserSampleGallery: React.FC<React.PropsWithChildren<Props>> = ({
  user,
  isOwnUser,
  dataHook,
  deviations,
  duperbrowseContextTitle,
  onWatch,
  className,
}) => {
  const { profile, stats, details, useridUuid, userId } = user;
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  // fire a bi event on the mobile gallery when scrolled, but only once
  const biData = useMemo(
    () => ({
      typeid: ResourceType.USER,
      itemid: userId,
    }),
    [userId]
  );

  // build a limited gallery of deviations wrapped in duperbrowse
  const thumbs = deviations && (
    <DeviationDuperbrowseContext
      streamId={`UserSampleGallery-${useridUuid}`}
      deviations={deviations}
      parent={{ title: duperbrowseContextTitle }}
    >
      {deviations.map((deviation, idx) => (
        <BiLoggerContextProvider key={`thumb-${idx}`} value={{ rank: idx }}>
          <DeviationLink deviation={deviation} className={s['thumb-link']}>
            <DeviationDumbView
              deviation={deviation}
              {...(isMobile ? MOBILE_THUMB_DIMS : DESKTOP_THUMB_DIMS)}
              {...(getDeviationType(deviation) ===
                DeviationTypes.LITERATURE && {
                className: s['literature-thumb'],
                coverLetterClassName: s['literature-thumb-cover-letter'],
              })}
            />
          </DeviationLink>
        </BiLoggerContextProvider>
      ))}
    </DeviationDuperbrowseContext>
  );
  const isGroup = getIsGroup(user);
  const tagline = profile?.tagline || '';
  const allow2LineName = !tagline && !isMobile;
  const joinedDistance = details && dateToDistanceTrans(details.joindate);
  const joinedDistanceLocaleKey = isMobile
    ? 'watchSuggestionsV2.userSampleGallery.joinedDateDistance.mobile'
    : 'watchSuggestionsV2.userSampleGallery.joinedDateDistance';
  return (
    <BiLoggerContextProvider value={{ sectionname: BI_SECTIONNAME }}>
      <Section
        className={classnames(s['root'], isGroup && s['is-group'], className)}
        data-hook={dataHook}
      >
        <div className={s['content']}>
          <UserAvatar
            user={user}
            size={isMobile ? 32 : 50}
            className={classnames(
              s['avatar'],
              user.usericonDefault && s['avatar-default']
            )}
          />

          <div className={s['user-info']}>
            <Heading className={s['user-link-heading']}>
              <UserLinkWithTooltip
                user={user}
                withTooltip
                className={
                  allow2LineName ? s['user-link-2-line'] : s['user-link']
                }
              />
            </Heading>
            {tagline && <span className={s['tagline']}>{tagline}</span>}
            <div className={s['stats']}>
              {stats && (
                <span className={s['stat-item']}>
                  {shortenNumber(stats.watchers, 0)}{' '}
                  {t('watchSuggestionsV2.userSampleGallery.watchers', {
                    formattedCount: stats.watchers,
                    count: stats.watchers,
                  })}
                </span>
              )}
              {joinedDistance && (
                <span className={s['stat-item']}>
                  {t(joinedDistanceLocaleKey, {
                    distance: t(joinedDistance.key, {
                      count: joinedDistance.value,
                    }),
                  })}
                </span>
              )}
            </div>
          </div>

          <Flex className={s['watch-join-buttons']}>
            {!isOwnUser && (
              <UserWatchGhostButton
                author={user}
                className={s['watch-button']}
                onWatch={onWatch}
              />
            )}
            {isGroup && (
              <GhostButton
                className={s['join-button']}
                size="small"
                variant="approval-green"
                href={Url.userLink(user.username)}
                aria-hidden={true}
              >
                {t('common.join')}
              </GhostButton>
            )}
          </Flex>
        </div>

        {isMobile ? (
          <Slider className={s['gallery']} slimSpacing onScrollBiData={biData}>
            {thumbs}
          </Slider>
        ) : (
          <Flex className={s['gallery']}>{thumbs}</Flex>
        )}
      </Section>
    </BiLoggerContextProvider>
  );
};

UserSampleGallery.displayName = 'UserSampleGallery';
export default UserSampleGallery;
